import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, XCircle, Clock, RefreshCw, Users, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/email-logs?limit=50`),
        fetch(`${API_URL}/api/email-logs/stats`)
      ]);

      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setLogs(logsData.logs || []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching email data:', error);
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Email logs refreshed');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading-spinner" />;
  }

  return (
    <div data-testid="email-logs-page">
      <div className="section-header">
        <div>
          <h2 className="section-title">Email Delivery Tracking</h2>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, marginTop: 4 }}>
            Monitor email digest delivery status and statistics
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-email-logs"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid" style={{ marginBottom: 24 }}>
          <div className="stat-card">
            <div className="stat-icon blue">
              <Mail size={22} />
            </div>
            <div className="stat-value">{stats.total_sent}</div>
            <div className="stat-label">Total Emails Sent</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <CheckCircle size={22} />
            </div>
            <div className="stat-value">{stats.successful}</div>
            <div className="stat-label">Successful ({stats.success_rate}%)</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon orange">
              <Clock size={22} />
            </div>
            <div className="stat-value">{stats.sent_today}</div>
            <div className="stat-label">Sent Today</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon red">
              <XCircle size={22} />
            </div>
            <div className="stat-value">{stats.failed}</div>
            <div className="stat-label">Failed</div>
          </div>
        </div>
      )}

      {/* Company Distribution */}
      {stats && stats.by_company && (
        <Card style={{ marginBottom: 24 }}>
          <CardContent style={{ padding: 20 }}>
            <h3 style={{ fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <BarChart3 size={18} />
              Emails by Company
            </h3>
            <div style={{ display: 'flex', gap: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 40, height: 40, borderRadius: 8, 
                  background: 'hsl(215 90% 95%)', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'hsl(215 90% 39%)', fontWeight: 700
                }}>
                  {stats.by_company.alephtav || 0}
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>Alephtav</div>
                  <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>Consultancy digests</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 40, height: 40, borderRadius: 8, 
                  background: 'hsl(142 72% 95%)', 
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'hsl(142 72% 29%)', fontWeight: 700
                }}>
                  {stats.by_company.mautech || 0}
                </div>
                <div>
                  <div style={{ fontWeight: 600 }}>Mau Tech</div>
                  <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>Hardware digests</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Logs Table */}
      <Card>
        <CardContent style={{ padding: 0 }}>
          {logs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Mail size={28} />
              </div>
              <h3 className="empty-state-title">No Email Logs Yet</h3>
              <p className="empty-state-text">
                Email delivery logs will appear here after running the tender scout and sending digests.
              </p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table" data-testid="email-logs-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Company</th>
                    <th>Recipients</th>
                    <th>Tenders</th>
                    <th>Sent At</th>
                    <th>Message ID</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, index) => (
                    <tr key={log._id || index} data-testid={`email-log-row-${index}`}>
                      <td>
                        {log.success ? (
                          <Badge className="status-completed" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <CheckCircle size={12} />
                            Delivered
                          </Badge>
                        ) : (
                          <Badge className="status-failed" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <XCircle size={12} />
                            Failed
                          </Badge>
                        )}
                      </td>
                      <td>
                        <span className={`company-badge-inline ${log.company === 'Alephtav' ? 'company-alephtav' : 'company-mautech'}`}>
                          {log.company}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Users size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
                          <span>{Array.isArray(log.recipients) ? log.recipients.length : 1}</span>
                        </div>
                        {Array.isArray(log.recipients) && (
                          <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginTop: 4 }}>
                            {log.recipients.slice(0, 2).join(', ')}
                            {log.recipients.length > 2 && ` +${log.recipients.length - 2} more`}
                          </div>
                        )}
                      </td>
                      <td>
                        <strong>{log.tender_count || 0}</strong>
                        <span style={{ color: 'hsl(var(--muted-foreground))' }}> tenders</span>
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {formatDate(log.sent_at || log.created_at)}
                      </td>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'hsl(var(--muted-foreground))' }}>
                        {log.message_id ? log.message_id.substring(0, 16) + '...' : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Details */}
      {logs.some(l => l.error) && (
        <Card style={{ marginTop: 24 }}>
          <CardContent style={{ padding: 20 }}>
            <h3 style={{ fontWeight: 600, marginBottom: 16, color: 'hsl(0 84% 50%)' }}>
              Recent Errors
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {logs.filter(l => l.error).slice(0, 5).map((log, index) => (
                <div 
                  key={index} 
                  style={{ 
                    padding: 12, 
                    background: 'hsl(0 84% 97%)', 
                    borderRadius: 8,
                    borderLeft: '3px solid hsl(0 84% 60%)'
                  }}
                >
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>{log.company} - {formatDate(log.created_at)}</div>
                  <div style={{ fontSize: 13, color: 'hsl(0 84% 40%)' }}>{log.error}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default EmailLogs;
